/**
 * StressTestPanel — Monte-Carlo and Driver Sensitivity analysis
 *
 * Phase 4.5 — redesign-p4-analacct-45b
 * Monte-Carlo distribution display and sensitivity table/chart.
 * Wires to existing /api/v1/analysis/monte-carlo and sensitivity endpoints.
 */

import React, { useState, useCallback } from 'react';
import {
  Activity,
  TrendingUp,
  AlertTriangle,
  Play,
  Loader2,
  BarChart3,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BRAND_NAME } from '@/config/brand';

// Import UI primitives
import { DataTable, Column } from '@/components/ui/DataTable';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

// Import analysis API
import {
  runMonteCarlo,
  runSensitivity,
  type MonteCarloResult,
  type SensitivityResult,
  type SensitivityParameter,
} from '@/services/analysisApi';

export interface StressTestPanelProps {
  reportId: string;
  reportData: any;
}

const ITERATION_OPTIONS = [1000, 2000, 5000];

const PARAMETERS: { value: SensitivityParameter; label: string }[] = [
  { value: 'payroll', label: 'Payroll' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'variable_expenses', label: 'Variable Expenses' },
];

// Simple bar chart for distribution
function DistributionChart({
  mean,
  p10,
  p90,
  stdDev,
}: {
  mean: number;
  p10: number;
  p90: number;
  stdDev: number;
}) {
  const range = p90 - p10;
  const scale = range > 0 ? 100 / range : 1;

  return (
    <div className="space-y-4">
      {/* Distribution bar */}
      <div className="relative h-8 rounded-lg bg-[var(--color-bg-elevated)] overflow-hidden">
        {/* P10 to P90 range */}
        <div
          className="absolute h-full bg-[rgba(255,102,0,0.3)]"
          style={{
            left: `${Math.max(0, (p10 - (mean - range)) * scale)}%`,
            width: `${Math.min(100, (p90 - p10) * scale)}%`,
          }}
        />
        {/* Mean marker */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-[var(--color-brand)]"
          style={{
            left: `${Math.min(100, Math.max(0, (mean - (mean - range)) * scale))}%`,
          }}
        />
      </div>

      {/* Labels */}
      <div className="flex justify-between text-xs text-[var(--color-text-muted)]">
        <span>P10: <FinancialValue value={p10} /></span>
        <span className="text-[var(--color-brand)]">Mean: <FinancialValue value={mean} /></span>
        <span>P90: <FinancialValue value={p90} /></span>
      </div>
    </div>
  );
}

// Sensitivity row component
interface SensitivityRow {
  parameter: string;
  variation: string;
  claimAmount: number;
  delta: number;
  deltaPercent: number;
}

export function StressTestPanel({ reportId, reportData }: StressTestPanelProps) {
  // Monte Carlo state
  const [iterations, setIterations] = useState(2000);
  const [mc, setMc] = useState<MonteCarloResult | null>(null);
  const [mcLoading, setMcLoading] = useState(false);
  const [mcError, setMcError] = useState<string | null>(null);

  // Sensitivity state
  const [parameter, setParameter] = useState<SensitivityParameter>('payroll');
  const [sens, setSens] = useState<SensitivityResult | null>(null);
  const [sensLoading, setSensLoading] = useState(false);
  const [sensError, setSensError] = useState<string | null>(null);

  const onRunMonteCarlo = useCallback(async () => {
    if (!reportId) return;
    setMcLoading(true);
    setMcError(null);
    try {
      const result = await runMonteCarlo(reportId, { iterations });
      setMc(result);
    } catch (err: any) {
      setMcError(err?.message || 'Failed to run Monte Carlo simulation');
    } finally {
      setMcLoading(false);
    }
  }, [reportId, iterations]);

  const onRunSensitivity = useCallback(async () => {
    if (!reportId) return;
    setSensLoading(true);
    setSensError(null);
    try {
      const result = await runSensitivity(reportId, { parameter });
      setSens(result);
    } catch (err: any) {
      setSensError(err?.message || 'Failed to run sensitivity analysis');
    } finally {
      setSensLoading(false);
    }
  }, [reportId, parameter]);

  // Prepare sensitivity table data from the API's results array
  // (SensitivityResult = { base_claim, parameter, results: SensitivityPoint[], ... }).
  const sensParamLabel = parameter.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const sensitivityTableData: SensitivityRow[] = sens
    ? sens.results.map((pt) => ({
        parameter: sensParamLabel,
        variation: `${pt.pct > 0 ? '+' : ''}${pt.pct}%`,
        claimAmount: pt.claim,
        delta: pt.claim - sens.base_claim,
        deltaPercent: sens.base_claim
          ? ((pt.claim - sens.base_claim) / sens.base_claim) * 100
          : 0,
      }))
    : [];

  const sensitivityColumns: Column<SensitivityRow>[] = [
    {
      key: 'variation',
      label: 'Variation',
      sortable: false,
    },
    {
      key: 'claimAmount',
      label: 'Claim Amount',
      sortable: false,
      render: (value) => <FinancialValue value={value} />,
    },
    {
      key: 'delta',
      label: 'Delta',
      sortable: false,
      render: (value) => (
        <span className={value >= 0 ? 'text-[#22C55E]' : 'text-[#888888]'}>
          <FinancialValue value={value} />
        </span>
      ),
    },
    {
      key: 'deltaPercent',
      label: 'Delta %',
      sortable: false,
      render: (value) => (
        <span className={value >= 0 ? 'text-[#22C55E]' : 'text-[#888888]'}>
          {value > 0 ? '+' : ''}
          {value.toFixed(1)}%
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Monte Carlo Section */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(255,102,0,0.12)]">
              <BarChart3 className="h-5 w-5 text-[var(--color-brand)]" />
            </div>
            <div>
              <h2
                className="text-lg font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Monte Carlo Simulation
              </h2>
              <p className="text-sm text-[var(--color-text-muted)]">
                Confidence interval analysis for claim robustness
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              {ITERATION_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setIterations(opt)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    iterations === opt
                      ? 'bg-[var(--color-brand)] text-white'
                      : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]'
                  )}
                >
                  {opt.toLocaleString()}
                </button>
              ))}
            </div>
            <Button
              onClick={onRunMonteCarlo}
              disabled={mcLoading}
              className="bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]"
            >
              {mcLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Simulation
                </>
              )}
            </Button>
          </div>
        </div>

        {mcError && (
          <div className="mb-4 rounded-md border border-[rgba(255,102,0,0.30)] bg-[rgba(255,102,0,0.12)] px-4 py-3 text-sm text-[var(--color-brand)]">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {mcError}
            </div>
          </div>
        )}

        {mc && (
          <div className="space-y-6">
            {/* Metrics */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <MetricCard
                label="Mean"
                value={<FinancialValue value={mc.results.mean} size="lg" />}
                accent
              />
              <MetricCard
                label="Median"
                value={<FinancialValue value={mc.results.median} size="lg" />}
              />
              <MetricCard
                label="Standard Deviation"
                value={<FinancialValue value={mc.results.std_dev} size="lg" />}
              />
              <MetricCard
                label="Iterations"
                value={mc.iterations.toLocaleString()}
                badge={{ text: 'Complete', variant: 'done' }}
              />
            </div>

            {/* Distribution */}
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
              <h3 className="mb-4 text-sm font-medium text-[var(--color-text-primary)]">
                Distribution (P10-P90 Range)
              </h3>
              <DistributionChart
                mean={mc.results.mean}
                p10={mc.results.ci_95_low}
                p90={mc.results.ci_95_high}
                stdDev={mc.results.std_dev}
              />
            </div>

            {/* Percentiles */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">P10 (Conservative)</p>
                <p className="mt-1 text-xl font-mono font-bold text-[var(--color-text-primary)]">
                  <FinancialValue value={mc.results.ci_95_low} />
                </p>
              </div>
              <div className="rounded-lg border border-[var(--color-brand)] bg-[rgba(255,102,0,0.05)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--color-brand)]">P50 (Median)</p>
                <p className="mt-1 text-xl font-mono font-bold text-[var(--color-brand)]">
                  <FinancialValue value={mc.results.median} />
                </p>
              </div>
              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-dim)]">P90 (Aggressive)</p>
                <p className="mt-1 text-xl font-mono font-bold text-[var(--color-text-primary)]">
                  <FinancialValue value={mc.results.ci_95_high} />
                </p>
              </div>
            </div>
          </div>
        )}

        {!mc && !mcLoading && !mcError && (
          <EmptyState
            title="No Simulation Run"
            description="Click 'Run Simulation' to generate Monte Carlo confidence intervals."
            icon={<BarChart3 />}
          />
        )}
      </div>

      {/* Driver Sensitivity Section */}
      <div className="rounded-[13px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[rgba(255,102,0,0.12)]">
              <TrendingUp className="h-5 w-5 text-[var(--color-brand)]" />
            </div>
            <div>
              <h2
                className="text-lg font-semibold text-[var(--color-text-primary)]"
                style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
              >
                Driver Sensitivity Analysis
              </h2>
              <p className="text-sm text-[var(--color-text-muted)]">
                How claim amount varies with key input drivers
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={parameter}
              onChange={(e) => setParameter(e.target.value as SensitivityParameter)}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)]"
            >
              {PARAMETERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <Button
              onClick={onRunSensitivity}
              disabled={sensLoading}
              className="bg-[var(--color-brand)] text-white hover:bg-[var(--color-brand-hover)]"
            >
              {sensLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  <Activity className="mr-2 h-4 w-4" />
                  Run Analysis
                </>
              )}
            </Button>
          </div>
        </div>

        {sensError && (
          <div className="mb-4 rounded-md border border-[rgba(255,102,0,0.30)] bg-[rgba(255,102,0,0.12)] px-4 py-3 text-sm text-[var(--color-brand)]">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {sensError}
            </div>
          </div>
        )}

        {sens && (
          <div className="space-y-6">
            {/* Sensitivity Table */}
            <DataTable
              columns={sensitivityColumns}
              data={sensitivityTableData}
              emptyMessage="No sensitivity data available"
            />

            {/* Sensitivity Metrics */}
            <div className="grid gap-4 sm:grid-cols-3">
              <MetricCard
                label="Base Claim"
                value={<FinancialValue value={sens.base_claim} />}
                badge={{ text: 'Reference', variant: 'neutral' }}
              />
              <MetricCard
                label="Maximum (+20%)"
                value={<FinancialValue value={Math.max(...sens.results.map((p) => p.claim))} />}
                badge={{ text: 'Upside', variant: 'done' }}
              />
              <MetricCard
                label="Minimum (-20%)"
                value={<FinancialValue value={Math.min(...sens.results.map((p) => p.claim))} />}
                badge={{ text: 'Downside', variant: 'attention' }}
              />
            </div>

            {/* Sensitivity Note */}
            <div className="flex items-start gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
              <Info className="h-5 w-5 flex-shrink-0 text-[var(--color-text-dim)]" />
              <p className="text-sm text-[var(--color-text-muted)]">
                Sensitivity analysis shows how the claim amount changes when{' '}
                {parameter.replace('_', ' ')} is varied by ±10% and ±20% from the base
                value. This helps identify which drivers have the most impact on the
                final claim.
              </p>
            </div>
          </div>
        )}

        {!sens && !sensLoading && !sensError && (
          <EmptyState
            title="No Sensitivity Analysis"
            description="Select a parameter and click 'Run Analysis' to see driver sensitivity."
            icon={<TrendingUp />}
          />
        )}
      </div>
    </div>
  );
}

export default StressTestPanel;
